import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { UserService } from './user.service';
import { ReplayProtectionService } from './replay-protection.service';
import { environment } from '../environments/environment';
import { from, Observable, of, switchMap, filter, take } from 'rxjs';
import { Store } from '@ngrx/store';
import { callApi } from '../state/actions';
import { ApiState } from '../state/reducer';

export interface AppState {
  api: ApiState;
}

@Injectable({ providedIn: 'root' })
export class ReduxApiHandlerService {
  private apiUrl = environment.apiUrl;
  private trackedEndpoints: string[] = [];

  constructor(
    private http: HttpClient,
    private userService: UserService,
    private replayProtection: ReplayProtectionService,
    private store: Store<AppState>
  ) {}

  callApi(method: string, endpoint: string, payload: any = {}): Observable<HttpResponse<any>> {
    const token = this.userService.getAccessToken();
    const userId = String(this.userService.getUserId());

    if (!this.trackedEndpoints.includes(endpoint)) {
      this.trackedEndpoints.push(endpoint);
    }

    if (method.toUpperCase() === 'GET') {
      return from(this.replayProtection.getReadHeaders(endpoint, userId)).pipe(
        switchMap(replayHeaders => {
          const headers = new HttpHeaders({ Authorization: `Bearer ${token}`, ...replayHeaders });
          return this.http.get<any>(`${this.apiUrl}${endpoint}`, { headers, observe: 'response' });
        })
      );
    }

    return from(this.replayProtection.getHeaders(payload, endpoint, userId)).pipe(
      switchMap(replayHeaders => {
        const headers = new HttpHeaders({
          Authorization: `Bearer ${token}`,
          ...replayHeaders
        });
        const options = { headers, observe: 'response' as const };
        switch (method.toUpperCase()) {
          case 'POST':  return this.http.post<any>(`${this.apiUrl}${endpoint}`, payload, options);
          case 'PUT':   return this.http.put<any>(`${this.apiUrl}${endpoint}`, payload, options);
          case 'DELETE': return this.http.delete<any>(`${this.apiUrl}${endpoint}`, options);
          default:      throw new Error(`Unsupported HTTP method: ${method}`);
        }
      })
    );
  }

  fetchWithCache<T>(endpoint: string, payload: any = {}, forceRefresh = false): Observable<T> {
    if (!this.trackedEndpoints.includes(endpoint)) {
      this.trackedEndpoints.push(endpoint);
    }

    return this.store.select(state => state.api.data[endpoint]).pipe(
      take(1),
      switchMap(cachedData => {
        if (cachedData && !forceRefresh) {
          return of(cachedData) as Observable<T>;
        } else {
          this.store.dispatch(callApi({ method: 'GET', endpoint, payload, key: endpoint }));

          return this.store.select(state => state.api.data[endpoint]).pipe(
            filter(d => !!d),
            take(1)
          ) as Observable<T>;
        }
      })
    );
  }

  forceRefreshAll(): void {
    this.trackedEndpoints.forEach(endpoint => {
      this.store.dispatch(callApi({ method: 'GET', endpoint, payload: {}, key: endpoint }));
    });
  }
}
